import { vars } from '@/shared/ui/theme';
import { reducedMotion } from '@/shared/ui/theme';

/**
 * LoginStepForm-specific styles.
 * Shared step/form styles come from AuthSceneLayout.
 */

export {
  stepFormContent,
  buttonGroup,
  errorBox,
  errorText,
} from '@/shared/ui/templates/AuthScene/AuthScene.css';

// ============================================
// EMAIL BADGE
// ============================================

import { style, globalStyle } from '@vanilla-extract/css';

export const emailBadge = style({
  display: 'flex',
  alignItems: 'center',
  gap: vars.spacing.gap.md,
  width: '100%',
  padding: '10px 14px',
  background: `linear-gradient(90deg, ${vars.surface.highlight.medium} 0%, ${vars.bright.card.subtle} 100%)`,
  border: `1px solid ${vars.surface.highlight.strong}`,
  borderRadius: '6px',
});

export const emailBadgeIcon = style({
  color: vars.bright.secondary.default,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
});

export const emailBadgeLabel = style({
  fontFamily: vars.typography.fontFamily.mono,
  fontSize: vars.typography.fontSize.sm,
  color: vars.bright.secondary.default,
  textShadow: `0 0 5px ${vars.bright.secondary.glow}`,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

// ============================================
// IDENTITY (확정된 인증 대상)
// ============================================

export const identityKicker = style({
  display: 'block',
  marginBottom: '6px',
  fontFamily: vars.typography.fontFamily.primary,
  fontSize: vars.typography.fontSize.xs,
  letterSpacing: vars.typography.letterSpacing.wide,
  color: vars.typography.textColor.label,
});

/**
 * 편집은 막되 <input> 자체는 남긴다 — 비밀번호 관리자가 짝지을 username 칸이 없으면
 * 자동완성·저장 제안이 붙지 않는다(BUG-50 이력). 그래서 입력칸 껍데기만 벗겨 뱃지에 앉힌다.
 */
export const identityValue = style({
  flex: 1,
  minWidth: 0,
  border: 'none',
  background: 'transparent',
  padding: 0,
  cursor: 'default',
  fontFamily: vars.typography.fontFamily.mono,
  fontSize: vars.typography.fontSize.sm,
  color: vars.bright.secondary.default,
  textShadow: `0 0 5px ${vars.bright.secondary.glow}`,
  textOverflow: 'ellipsis',
});

export const identityChangeButton = style({
  flexShrink: 0,
  padding: '3px 9px',
  border: `1px solid ${vars.surface.highlight.strong}`,
  borderRadius: '4px',
  background: 'transparent',
  cursor: 'pointer',
  fontFamily: vars.typography.fontFamily.mono,
  fontSize: vars.typography.fontSize.xs,
  color: vars.bright.secondary.default,
  transition: `border-color ${vars.animation.duration.fast} ${vars.animation.easing.easeOut}`,
  selectors: {
    '&:hover:not(:disabled)': {
      borderColor: vars.surface.border.glow,
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  },

  '@media': {
    [reducedMotion]: {
      transition: 'none',
    },
  },
});

// ============================================
// SIGNUP NOTE
// ============================================

export const signupNote = style({
  padding: '12px 16px',
  background: `linear-gradient(90deg, ${vars.surface.highlight.medium} 0%, ${vars.surface.highlight.faint} 100%)`,
  border: `1px solid ${vars.surface.highlight.strong}`,
  borderLeft: `3px solid ${vars.bright.status.pending}`,
  borderRadius: '4px',
  fontFamily: vars.typography.fontFamily.primary,
  fontSize: vars.typography.fontSize.xs,
  color: vars.bright.status.pending,
  lineHeight: vars.typography.lineHeight.normal,
  letterSpacing: vars.typography.letterSpacing.wide,
});

export const signupTitle = style({
  fontWeight: vars.typography.fontWeight.semibold,
  marginBottom: '4px',
});

export const signupDescription = style({
  color: vars.typography.textColor.label,
});

// ============================================
// BACK ARROW ICON
// ============================================

export const backArrowIcon = style({
  display: 'inline-flex',
  alignItems: 'center',
  flexShrink: 0,
});

// ============================================
// OTP INPUT
// ============================================

export const otpInput = style({
  fontFamily: vars.typography.fontFamily.mono,
  fontSize: vars.typography.fontSize.xl,
  letterSpacing: '0.5em',
  textAlign: 'center',
});

globalStyle(`${otpInput}::placeholder`, {
  fontSize: vars.typography.fontSize.sm,
  letterSpacing: vars.typography.letterSpacing.wide,
});

// ============================================
// METHOD SELECTION
// ============================================

export const methodGroup = style({
  display: 'flex',
  flexDirection: 'column',
  gap: vars.spacing.gap.md,
  width: '100%',
});

// ============================================
// DUPLICATE NAME NOTICE
// ============================================

export const duplicateNotice = style({
  padding: '12px 16px',
  background: `linear-gradient(90deg, ${vars.surface.highlight.medium} 0%, ${vars.surface.highlight.faint} 100%)`,
  border: `1px solid ${vars.surface.highlight.strong}`,
  borderLeft: `3px solid ${vars.bright.status.pending}`,
  borderRadius: '4px',
  fontFamily: vars.typography.fontFamily.primary,
  fontSize: vars.typography.fontSize.xs,
  color: vars.bright.status.pending,
  lineHeight: vars.typography.lineHeight.normal,
  letterSpacing: vars.typography.letterSpacing.wide,
});

// ============================================
// CANDIDATE LIST (동명이인 선택)
// ============================================

export const candidateList = style({
  display: 'flex',
  flexDirection: 'column',
  gap: vars.spacing.gap.sm,
  width: '100%',
});

export const candidateButton = style({
  display: 'flex',
  alignItems: 'center',
  gap: vars.spacing.gap.md,
  width: '100%',
  padding: '10px 14px',
  background: `linear-gradient(90deg, ${vars.surface.highlight.medium} 0%, ${vars.bright.card.subtle} 100%)`,
  border: `1px solid ${vars.surface.highlight.strong}`,
  borderRadius: '6px',
  cursor: 'pointer',
  fontFamily: vars.typography.fontFamily.mono,
  fontSize: vars.typography.fontSize.sm,
  color: vars.bright.secondary.default,
  textShadow: `0 0 5px ${vars.bright.secondary.glow}`,
  textAlign: 'left',
  transition: `border-color ${vars.animation.duration.fast} ${vars.animation.easing.easeOut}`,
  selectors: {
    '&:hover:not(:disabled)': {
      borderColor: vars.surface.border.glow,
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  },

  '@media': {
    [reducedMotion]: {
      transition: 'none',
    },
  },
});

export const candidateButtonIcon = style({
  color: vars.bright.secondary.default,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
});

// ============================================
// OTP RESEND
// ============================================

export const resendContainer = style({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: vars.spacing.gap.sm,
});

export const cooldownText = style({
  fontFamily: vars.typography.fontFamily.primary,
  fontSize: vars.typography.fontSize.xs,
  color: vars.typography.textColor.label,
});

/** method 스텝의 잠긴 인증코드 버튼 아래 붙는 대기 문구. otp 스텝 문구와 같은 톤이되,
 *  가로 flex 컨테이너 없이 홀로 서므로 가운데 정렬을 직접 갖는다. */
export const methodCooldownText = style([
  cooldownText,
  {
    textAlign: 'center',
    marginTop: `calc(-1 * ${vars.spacing.gap.sm})`,
  },
]);

/** 자동으로 연 패스키가 끝나지 않았다는 안내 — 동명이인 안내와 같은 주의 톤 */
export const methodNotice = style([duplicateNotice]);

/** 「등록되지 않은 이름」 아래 고정 안내 — 대기 문구와 같은 보조 톤(가입 확인 뒤 부서 배정 대기, 87bac89b) */
export const unregisteredHint = style([
  cooldownText,
  {
    margin: 0,
    lineHeight: vars.typography.lineHeight.normal,
  },
]);

/** 동명이인 후보 중 이 브라우저가 지난번에 로그인한 계정 표시 */
export const lastAccountTag = style({
  marginLeft: 'auto',
  flexShrink: 0,
  fontSize: vars.typography.fontSize['2xs'],
  letterSpacing: vars.typography.letterSpacing.wide,
  color: vars.bright.secondary.glow,
});

export const gmailLink = style({
  justifySelf: 'center',
  alignSelf: 'center',
  fontFamily: vars.typography.fontFamily.primary,
  fontSize: vars.typography.fontSize.sm,
  color: vars.bright.secondary.default,
  textUnderlineOffset: '3px',
});
